/**
 * venueService.ts
 *
 * All data access goes through this layer.
 *
 * MULTI-CITY SUPPORT:
 * Each city can optionally define a metro area — a list of nearby cities
 * that are included in that city's feed (e.g. Covington/Newport for Cincinnati).
 * If no metro is defined, only exact city matches are returned.
 */
import { supabase } from '../lib/supabase'
import type { Venue, HappyHourSchedule } from '../types'

// ─────────────────────────────────────────────
// METRO AREAS
// Add new cities here as you expand.
// ─────────────────────────────────────────────

const METRO_AREAS: Record<string, string[]> = {
  'Cincinnati': [
    'Cincinnati', 'Covington', 'Newport', 'Bellevue',
    'Dayton', 'Fort Thomas', 'Alexandria', 'Norwood',
    'Hyde Park', 'Anderson Township', 'Fort Wright',
    'Milford', 'Newtown', 'Loveland', 'Mason', 'Fairfield',
  ],
  // Add new cities as you expand:
  // 'Columbus': ['Columbus', 'Dublin', 'Westerville', 'Gahanna'],
  // 'Cleveland': ['Cleveland', 'Lakewood', 'Cleveland Heights'],
}

function getCitiesForFeed(city: string): string[] {
  // Check for exact metro match first
  if (METRO_AREAS[city]) return METRO_AREAS[city]
  // Check case-insensitive
  const match = Object.keys(METRO_AREAS).find(
    k => k.toLowerCase() === city.toLowerCase()
  )
  if (match) return METRO_AREAS[match]
  // No metro defined — just use the city itself
  return [city]
}

// ─────────────────────────────────────────────
// READ
// ─────────────────────────────────────────────

// ─────────────────────────────────────────────
// CITY FEEDS
// Add a new city here when you're ready to expand.
// Each city gets its own center point and radius.
// The app's city selector drives which feed loads.
// ─────────────────────────────────────────────

interface CityFeed {
  label:       string   // display name
  center:      { lat: number; lng: number }
  radiusMiles: number
}

const CITY_FEEDS: Record<string, CityFeed> = {
  'Cincinnati': {
    label:       'Cincinnati',
    center:      { lat: 39.1031, lng: -84.5120 },
    radiusMiles: 30,   // covers full tristate: NKY, SE Indiana, suburbs
  },
  // Uncomment when ready to launch in new cities:
  // 'Columbus': {
  //   label:       'Columbus',
  //   center:      { lat: 39.9612, lng: -82.9988 },
  //   radiusMiles: 25,
  // },
  // 'Lexington': {
  //   label:       'Lexington',
  //   center:      { lat: 38.0406, lng: -84.5037 },
  //   radiusMiles: 20,
  // },
  // 'Indianapolis': {
  //   label:       'Indianapolis',
  //   center:      { lat: 39.7684, lng: -86.1581 },
  //   radiusMiles: 25,
  // },
  // 'Louisville': {
  //   label:       'Louisville',
  //   center:      { lat: 38.2527, lng: -85.7585 },
  //   radiusMiles: 25,
  // },
}

// Haversine distance in miles
function milesBetween(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3958.8
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLng = ((lng2 - lng1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

export async function getVenues(city = 'Cincinnati'): Promise<Venue[]> {
  // Look up the feed config for this city, fall back to Cincinnati
  const feed = CITY_FEEDS[city] ?? CITY_FEEDS['Cincinnati']

  // Bounding box around the metro center, computed from lat/lng — NOT
  // from a hand-maintained city-name list (METRO_AREAS). A name list
  // silently drops any venue whose `city` column doesn't exactly match
  // a string in the array — that's exactly what happened to Mason and
  // Fairfield: real venues, correct data, just never added to the list.
  // Filtering by coordinates instead is self-maintaining: any venue
  // with correct lat/lng is included automatically, and every venue
  // already needs correct lat/lng for the map/distance sort to work —
  // so there's no second thing to remember to keep in sync.
  // 1° latitude ≈ 69 miles everywhere; 1° longitude ≈ 69·cos(latitude) miles.
  const latDelta = feed.radiusMiles / 69
  const lngDelta = feed.radiusMiles / (69 * Math.cos((feed.center.lat * Math.PI) / 180))

  // NO SAMPLE DATA FALLBACK — this is intentional.
  // Serving fake venues with fake deals on a DB error is a trust-killer:
  // a real user could drive to a bar for a deal we invented. On failure we
  // throw so AppContext can show an honest error state; the localStorage
  // venue cache (venueCache.ts) covers most transient failures with real,
  // slightly-stale data instead.
  const [boxResult, noCoordsResult] = await Promise.all([
    supabase
      .from('venues_with_schedules')
      .select('*')
      .gte('latitude', feed.center.lat - latDelta)
      .lte('latitude', feed.center.lat + latDelta)
      .gte('longitude', feed.center.lng - lngDelta)
      .lte('longitude', feed.center.lng + lngDelta)
      .order('is_featured', { ascending: false })
      .order('name'),
    // Venues missing coordinates entirely can't be geo-filtered at all —
    // surface them too rather than silently hiding a newly-added,
    // not-yet-geocoded venue from admins and users. Cheap query at this
    // table size; not worth optimizing away.
    supabase
      .from('venues_with_schedules')
      .select('*')
      .or('latitude.is.null,longitude.is.null'),
  ])

  if (boxResult.error) throw new Error(boxResult.error.message)
  if (noCoordsResult.error) throw new Error(noCoordsResult.error.message)

  const combined = [...(boxResult.data || []), ...(noCoordsResult.data || [])] as Venue[]

  // De-dupe defensively (the two queries shouldn't overlap, but don't
  // trust that blindly), then do the final precise circle trim — the
  // bounding box above is a square approximation, this cuts the corners
  // it let through that are actually outside the true radius.
  const seen = new Set<string>()
  return combined.filter(v => {
    if (seen.has(v.id)) return false
    seen.add(v.id)
    if (!v.latitude || !v.longitude) return true
    return milesBetween(
      feed.center.lat, feed.center.lng,
      v.latitude, v.longitude
    ) <= feed.radiusMiles
  })
}

// Returns all cities that have an active feed
// Used to populate a city selector in the UI
export function getAvailableCities(): string[] {
  return Object.keys(CITY_FEEDS)
}

export async function getVenueById(id: string): Promise<Venue | null> {
  try {
    const { data, error } = await supabase
      .from('venues_with_schedules')
      .select('*')
      .eq('id', id)
      .single()
    if (error) return null
    return data as Venue
  } catch {
    return null
  }
}

export async function getVenuesByNeighborhood(
  neighborhood: string,
  city = 'Cincinnati'
): Promise<Venue[]> {
  const citiesToFetch = getCitiesForFeed(city)
  try {
    const { data, error } = await supabase
      .from('venues_with_schedules')
      .select('*')
      .in('city', citiesToFetch)
      .eq('neighborhood', neighborhood)
      .order('is_featured', { ascending: false })
      .order('name')
    if (error) throw error
    return (data ?? []) as Venue[]
  } catch {
    return []
  }
}

// ─────────────────────────────────────────────
// WRITE
// ─────────────────────────────────────────────

export async function addVenue(
  venue: Omit<Venue, 'id' | 'created_at' | 'updated_at' | 'schedules'>
): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('venues')
      .insert([{ ...venue }])
      .select('id')
      .single()
    if (error) throw error
    return data?.id ?? null
  } catch {
    return null
  }
}

export async function addSchedule(
  schedule: Omit<HappyHourSchedule, 'id' | 'created_at' | 'updated_at'>
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('happy_hour_schedules')
      .insert([{ ...schedule }])
    return !error
  } catch {
    return false
  }
}
